import { ReactNode, useCallback } from 'react';
import styled from '@emotion/styled';
import { Formik, FormikProps, FormikConfig, FormikHelpers } from 'formik';
import { Form, Radio, Switch } from 'antd';
import { Descendant } from 'slate';
import dayjs from 'lib/dayjs';
import { set } from 'lodash-es';

import FormField, { SmartFieldProps } from 'components/FormField';
import HintInput from 'components/HintInput';
import { getSegmentsLength } from 'components/TemplateEditor/Editor';
import { useTranslate } from '@tolgee/react';
import {
  StyledForm,
  ColumnFormItem,
  ResultText,
} from 'views/Setting/AutoEmit/Refund/components/TaskForm';
import { NegativeResult } from 'views/Setting/AutoEmit/components/Result';
import ContextEditor from 'views/Setting/AutoEmit/components/ContextEditor';
import { mustache2SlateValue } from 'components/TemplateEditor/mustacheUtils';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import EditorPreviewer from 'components/EditorPreviewer';
import { DatePicker } from 'components/DatePicker';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export interface PolicyFormValues
  extends Omit<
    Marketing.Policy,
    'id' | 'taskId' | 'roundId' | 'priority' | 'bilingualExecuteContext' | 'executeItemContext'
  > {
  id?: Marketing.Policy['id'];
  bilingualExecuteContext: {
    content: Descendant[];
    translatedContent: Descendant[];
  };
  executeItemContext: {
    itemId: string;
    customizeContext?: Descendant[];
  }[];
}

type FV = PolicyFormValues;

export type PolicyFormHelpers = FormikHelpers<FV>;

type FormChildren<FV> = ReactNode | ((form: ReactNode, formProps: FormikProps<FV>) => ReactNode);

export interface PolicyFormProps extends Omit<FormikConfig<FV>, 'initialValues'> {
  displayName?: boolean;
  initialValues?: FV;
  children?: FormChildren<FV>;
  existNameList: string[];
  region: General.UpperCaseRegion;
}

const defaultInitialValues: FV = {
  name: '',
  enable: true,
  termOfValidity: {
    type: 'LONG_TERM',
    dateTimeQuantum: null,
    timeQuantumGroup: null,
  },
  filterItem: {
    type: 'NO_FILTER',
    specificSPUs: [],
    specificSKUs: [],
    specificCategories: [],
  },
  bilingualExecuteContext: {
    content: mustache2SlateValue(''),
    translatedContent: mustache2SlateValue(''),
  },
  executeItemContext: [],
  cancelTradeConfig: {
    shippingStatus: ['TO_PROCESS'],
    autoCancel: false,
    autoCancelLimitHour: 0,
    contents: [],
  },
  limitItem: {
    type: 'ALL',
    specificItems: [],
  },
};

const NameInput = styled(HintInput)`
  width: 340px;
`;

const InlineItem = styled.span`
  display: inline-block;
  padding: 0 8px;
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item')} {
    width: 90px;
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('form-item-explain')} {
    white-space: nowrap;
  }
`;

const NoErrorFormItem = styled(FormField)`
  margin-bottom: 5px;
`;

const NoWrap = styled.div`
  white-space: nowrap;
`;

const Alert = styled.div`
  display: inline-block;
  height: 32px;
  line-height: 32px;
  padding: 0 16px;
  background: var(--fett-surface-warning-bg);
`;

const nameLimitLength = 20;
const executeContextLimitLength = 500;

const getSplitSegments = (segments: Descendant[]) => {
  return segments
    .reduce<Descendant[][]>(
      (res, cur) => {
        if (cur.type === CustomSlateNodeType.DIVIDING_LINE) {
          res.push([]);

          return res;
        }

        res[res.length - 1].push(cur);

        return res;
      },
      [[]],
    )
    .filter((v) => getSegmentsLength(v));
};

export const getResultContexts = (executeContext: AutoEmit.ExecuteContext<Descendant[]>) => {
  const { content, translatedContent } = executeContext;

  const translatedSplitContent = getSplitSegments(translatedContent);

  if (translatedSplitContent.length) {
    return translatedSplitContent;
  }

  const splitContent = getSplitSegments(content);

  if (splitContent.length) {
    return splitContent;
  }

  return undefined;
};

function PolicyForm(props: PolicyFormProps) {
  const {
    displayName = true,
    initialValues = defaultInitialValues,
    children,
    existNameList,
    region,
    ...otherProps
  } = props;

  const isRenderProps = typeof children === 'function';

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const regionLabel = getRegionLabel(region);

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const {
        name,
        termOfValidity: { type: termOfValidityType, dateTimeQuantum, timeQuantumGroup },
        bilingualExecuteContext: { content, translatedContent },
      } = values;

      if (displayName) {
        if (!name) {
          errors.name = t('marketing.请输入任务名称');
        } else if (name.length > nameLimitLength) {
          errors.name = t('marketing.任务名称不得超过x个字', { x: nameLimitLength });
        } else if (existNameList.some((existName) => existName === name)) {
          errors.name = t('marketing.任务名称已存在');
        }
      }

      if (termOfValidityType === 'DATE_TIME_QUANTUM') {
        const { startDateTime, endDateTime } = dateTimeQuantum || {};

        if (!startDateTime || !endDateTime) {
          set(errors, 'termOfValidity.dateTimeQuantum', t('marketing.请选择起止时间'));
        } else if (dayjs(endDateTime).isBefore(dayjs())) {
          set(errors, 'termOfValidity.dateTimeQuantum', t('marketing.结束时间不可早于当前时间'));
        }
      } else if (termOfValidityType === 'TIME_QUANTUM_GROUP') {
        const { startDate, endDate, startTime, endTime } = timeQuantumGroup || {};

        if (!startDate || !endDate || !startTime || !endTime) {
          set(errors, 'termOfValidity.timeQuantumGroup', t('marketing.请选择固定时间段'));
        } else if (
          startDate === endDate &&
          dayjs(startTime, 'HH:mm:ss').isAfter(dayjs(endTime, 'HH:mm:ss'))
        ) {
          set(errors, 'termOfValidity.timeQuantumGroup', t('marketing.跨天任务日期范围需大于一天'));
        } else if (dayjs(`${endDate} ${endTime}`).isBefore(dayjs())) {
          set(errors, 'termOfValidity.timeQuantumGroup', t('marketing.结束时间不可早于当前时间'));
        }
      }

      const contentLength = getSegmentsLength(content);
      const translatedContentLength = getSegmentsLength(translatedContent);

      if (contentLength === 0) {
        errors.bilingualExecuteContext = t('marketing.请输入下发内容');
      } else if (
        contentLength > executeContextLimitLength ||
        translatedContentLength > executeContextLimitLength
      ) {
        errors.bilingualExecuteContext = t('marketing.下发内容不可超出x个字', {
          x: executeContextLimitLength,
        });
      }

      return errors;
    },
    [existNameList, displayName, t],
  );

  return (
    <Formik enableReinitialize initialValues={initialValues} validate={validate} {...otherProps}>
      {(props) => {
        const {
          values: {
            termOfValidity: { type: termOfValidityType },
            bilingualExecuteContext,
          },
        } = props;

        const resultContexts = getResultContexts(bilingualExecuteContext);

        const form = (
          <StyledForm colon>
            <div>
              {displayName && (
                <ColumnFormItem>
                  <FormField<FV['name']> required name="name" label={t('marketing.任务名称')}>
                    {({ field }) => {
                      return (
                        <NameInput
                          allowClear
                          limit={nameLimitLength}
                          maxLength={nameLimitLength}
                          placeholder={t('marketing.请输入任务名称')}
                          {...field}
                        />
                      );
                    }}
                  </FormField>
                </ColumnFormItem>
              )}
              <FormField<FV['enable']> required name="enable" label={t('marketing.启用状态')}>
                {({ field: { value, onChange } }) => {
                  return (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      checkedChildren={t('marketing.启用中')}
                      unCheckedChildren={t('marketing.未启用')}
                    />
                  );
                }}
              </FormField>
              <ColumnFormItem>
                <Form.Item required label={t('marketing.有效期')}>
                  <Alert>
                    {t('marketing.任务执行时间以当前region站点时区为准', { region: regionLabel })}
                  </Alert>
                  <NoErrorFormItem name="termOfValidity.type">
                    {({
                      field: { value, onChange },
                    }: SmartFieldProps<FV['termOfValidity']['type']>) => {
                      return (
                        <Radio.Group value={value} onChange={(e) => onChange(e.target.value)}>
                          <Radio value="LONG_TERM">{t('marketing.长期')}</Radio>
                          <Radio value="DATE_TIME_QUANTUM">{t('marketing.起止')}</Radio>
                          <Radio value="TIME_QUANTUM_GROUP">{t('marketing.固定时段')}</Radio>
                        </Radio.Group>
                      );
                    }}
                  </NoErrorFormItem>
                  {termOfValidityType === 'DATE_TIME_QUANTUM' && (
                    <FormField<
                      FV['termOfValidity']['dateTimeQuantum']
                    > name="termOfValidity.dateTimeQuantum">
                      {({ field: { value, onChange, ...others } }) => {
                        const { startDateTime, endDateTime } = value || {};

                        return (
                          <DatePicker.RangePicker
                            showTime
                            format="YYYY-MM-DD HH:mm"
                            value={[
                              startDateTime ? dayjs(startDateTime) : null,
                              endDateTime ? dayjs(endDateTime) : null,
                            ]}
                            onChange={(range) => {
                              const [start, end] = range || [];
                              onChange({
                                startDateTime: start
                                  ? dayjs(start).format('YYYY-MM-DD HH:mm')
                                  : null,
                                endDateTime: end ? dayjs(end).format('YYYY-MM-DD HH:mm') : null,
                              });
                            }}
                            disabledDate={(currentDate) => {
                              return currentDate.isBefore(dayjs().startOf('days'));
                            }}
                            {...others}
                          />
                        );
                      }}
                    </FormField>
                  )}
                  {termOfValidityType === 'TIME_QUANTUM_GROUP' && (
                    <FormField<
                      FV['termOfValidity']['timeQuantumGroup']
                    > name="termOfValidity.timeQuantumGroup">
                      {({ field: { value, onChange, onBlur } }) => {
                        const { startDate, endDate, startTime, endTime } = value || {};
                        return (
                          <NoWrap onBlur={onBlur}>
                            {t('marketing.起止日期')}
                            <InlineItem>
                              <DatePicker.RangePicker
                                value={[
                                  startDate ? dayjs(startDate) : null,
                                  endDate ? dayjs(endDate) : null,
                                ]}
                                onChange={(range) => {
                                  const [start, end] = range || [];
                                  onChange({
                                    ...value,
                                    startDate: start ? dayjs(start).format('YYYY-MM-DD') : null,
                                    endDate: end ? dayjs(end).format('YYYY-MM-DD') : null,
                                  });
                                }}
                                disabledDate={(currentDate) => {
                                  return currentDate.isBefore(dayjs().startOf('days'));
                                }}
                              />
                            </InlineItem>
                            {t('marketing.每天')}
                            <InlineItem>
                              <DatePicker.RangePicker
                                picker="time"
                                mode={undefined}
                                order={false}
                                format="HH:mm"
                                value={[
                                  startTime ? dayjs(startTime, 'HH:mm:ss') : null,
                                  endTime ? dayjs(endTime, 'HH:mm:ss') : null,
                                ]}
                                onChange={(range) => {
                                  const [start, end] = range || [];
                                  onChange({
                                    ...value,
                                    startTime: start
                                      ? dayjs(start).startOf('minutes').format('HH:mm:ss')
                                      : null,
                                    endTime: end
                                      ? dayjs(end).startOf('minutes').format('HH:mm:ss')
                                      : null,
                                  });
                                }}
                              />
                            </InlineItem>
                          </NoWrap>
                        );
                      }}
                    </FormField>
                  )}
                </Form.Item>
              </ColumnFormItem>
              <h3>{t('reviewSetting.发送内容')}</h3>
              <FormField<FV['bilingualExecuteContext']> name="bilingualExecuteContext">
                {({ field: { value, onChange } }) => {
                  return (
                    <ContextEditor
                      region={region}
                      context={value}
                      onContextChange={onChange}
                      limit={executeContextLimitLength}
                      allowDividing
                      concepts={[
                        {
                          id: '买家昵称',
                          label: t('marketing.买家昵称'),
                        },
                      ]}
                    />
                  );
                }}
              </FormField>
            </div>
            <span>
              <h4>{t('marketing.示例')}</h4>
              <NegativeResult
                text={
                  resultContexts
                    ? resultContexts.map((context) => {
                        return (
                          <ResultText>
                            <EditorPreviewer value={context} />
                          </ResultText>
                        );
                      })
                    : undefined
                }
              />
            </span>
          </StyledForm>
        );

        return isRenderProps
          ? (children as (form: ReactNode, formProps: FormikProps<FV>) => ReactNode)(form, props)
          : form;
      }}
    </Formik>
  );
}

export default PolicyForm;
